import { useState, useEffect } from "react";
import {
  Table,
  Card,
  Spin,
  Tag,
  Button,
  Typography,
  message,
  Tooltip,
  Empty,
} from "antd";
import {
  DownloadOutlined,
  ReloadOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabase";
import { useCompany } from "../hooks/useCompany";
import { PaymentTermsSettings } from "../components/invoices/PaymentTermsSettings";
import type { ColumnsType } from "antd/es/table";

const { Title } = Typography;

const Container = styled.div`
  padding: 24px;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const StyledCard = styled(Card)`
  .ant-card-body {
    padding: 0;
  }
`;

interface Invoice {
  id: string;
  invoice_id: string;
  reference: string;
  state: string;
  invoice_date: string;
  due_date: string;
  total_price_incl_tax: string;
  total_price_excl_tax: string;
  total_unpaid: string;
  currency: string;
  contact: {
    id: string;
    company_name: string;
  };
  details: Array<{
    id: string;
    description: string;
    amount: string;
    price: string;
    total_price_excl_tax: string;
    total_price_incl_tax: string;
  }>;
}

const stateColors: Record<string, string> = {
  draft: "default",
  open: "processing",
  scheduled: "warning",
  pending_payment: "warning",
  late: "error",
  reminded: "error",
  paid: "success",
  uncollectible: "default",
};

const stateLabels: Record<string, string> = {
  draft: "Draft",
  open: "Open",
  scheduled: "Scheduled",
  pending_payment: "Pending Payment",
  late: "Overdue",
  reminded: "Reminded",
  paid: "Paid",
  uncollectible: "Uncollectible",
};

export const Invoices = () => {
  const { t } = useTranslation();
  const { selectedCompany, companyDetails, companyDetailsLoading } =
    useCompany();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchInvoices = async (showRefreshMessage = false) => {
    // Don't proceed if company details are still loading
    if (companyDetailsLoading) {
      return;
    }

    // Don't proceed if company details aren't loaded yet
    if (!companyDetails) {
      setLoading(false);
      return;
    }

    // Check if the company has Moneybird integration
    if (!companyDetails.moneybird_contact_id) {
      message.warning(t("invoices.noMoneybirdIntegration"));
      setLoading(false);
      return;
    }

    try {
      if (showRefreshMessage) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error("No session");

      const response = await supabase.functions.invoke(
        "fetch-moneybird-invoices",
        {
          body: { company_id: selectedCompany },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (response.error) {
        throw new Error(response.error.message || "Failed to fetch invoices");
      }

      setInvoices(response.data.invoices || []);

      if (showRefreshMessage) {
        message.success(t("invoices.refreshSuccess"));
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      message.error(t("invoices.fetchError"));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (selectedCompany && !companyDetailsLoading) {
      fetchInvoices();
    }
  }, [
    selectedCompany,
    companyDetailsLoading,
    companyDetails?.moneybird_contact_id,
  ]);

  const handleDownloadInvoice = async (invoice: Invoice) => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error("No session");

      // Call our edge function to download the PDF
      const response = await supabase.functions.invoke(
        "download-moneybird-invoice-pdf",
        {
          body: {
            invoice_id: invoice.id,
            company_id: selectedCompany,
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (response.error) {
        console.error("PDF download error:", response.error);
        message.error(t("invoices.downloadError"));
        return;
      }

      if (response.data && response.data.pdf_base64) {
        // Convert base64 to blob and download
        const byteCharacters = atob(response.data.pdf_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });

        // Create download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `invoice-${invoice.invoice_id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        message.success(t("invoices.downloadSuccess"));
      } else {
        message.error(t("invoices.downloadError"));
      }
    } catch (error) {
      console.error("Error downloading invoice PDF:", error);
      message.error(t("invoices.downloadError"));
    }
  };

  const formatCurrency = (amount: string, currency: string) => {
    const numAmount = parseFloat(amount);
    return new Intl.NumberFormat("nl-NL", {
      style: "currency",
      currency: currency || "EUR",
    }).format(numAmount);
  };

  const columns: ColumnsType<Invoice> = [
    {
      title: t("invoices.invoiceNumber"),
      dataIndex: "invoice_id",
      key: "invoice_id",
      width: 130,
    },
    {
      title: t("invoices.reference"),
      dataIndex: "reference",
      key: "reference",
      width: 150,
      ellipsis: true,
    },
    {
      title: t("invoices.status"),
      dataIndex: "state",
      key: "state",
      width: 100,
      render: (state: string) => (
        <Tag color={stateColors[state] || "default"}>
          {stateLabels[state] || state}
        </Tag>
      ),
    },
    {
      title: t("invoices.invoiceDate"),
      dataIndex: "invoice_date",
      key: "invoice_date",
      width: 110,
      render: (date: string) => dayjs(date).format("DD-MM-YYYY"),
    },
    {
      title: t("invoices.dueDate"),
      dataIndex: "due_date",
      key: "due_date",
      width: 110,
      render: (date: string) => dayjs(date).format("DD-MM-YYYY"),
    },
    {
      title: t("invoices.totalExclTax"),
      dataIndex: "total_price_excl_tax",
      key: "total_price_excl_tax",
      width: 130,
      align: "right",
      render: (amount: string, record: Invoice) =>
        formatCurrency(amount, record.currency),
    },
    {
      title: t("invoices.totalInclTax"),
      dataIndex: "total_price_incl_tax",
      key: "total_price_incl_tax",
      width: 130,
      align: "right",
      render: (amount: string, record: Invoice) =>
        formatCurrency(amount, record.currency),
    },
    {
      title: t("invoices.unpaid"),
      dataIndex: "total_unpaid",
      key: "total_unpaid",
      width: 120,
      align: "right",
      render: (amount: string, record: Invoice) => {
        const numAmount = parseFloat(amount);
        return (
          <span style={{ color: numAmount > 0 ? "#ff4d4f" : undefined }}>
            {formatCurrency(amount, record.currency)}
          </span>
        );
      },
    },
    {
      title: t("common.actions"),
      key: "actions",
      width: 80,
      fixed: "right",
      render: (_, record: Invoice) => (
        <Tooltip title={t("invoices.downloadInvoice")}>
          <Button
            type="text"
            icon={<DownloadOutlined />}
            onClick={() => handleDownloadInvoice(record)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Container>
      <HeaderRow>
        <Title level={2}>
          <FileTextOutlined style={{ marginRight: 8 }} />
          {t("invoices.title")}
        </Title>
        <Button
          type="primary"
          icon={<ReloadOutlined />}
          onClick={() => fetchInvoices(true)}
          loading={refreshing}
        >
          {t("invoices.refresh")}
        </Button>
      </HeaderRow>

      <div style={{ marginBottom: 24 }}>
        <PaymentTermsSettings />
      </div>

      <StyledCard>
        {loading ? (
          <div style={{ padding: 24, textAlign: "center" }}>
            <Spin />
          </div>
        ) : invoices.length === 0 ? (
          <Empty
            style={{ padding: 24 }}
            description={t("invoices.noInvoices")}
          />
        ) : (
          <Table
            dataSource={invoices}
            columns={columns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        )}
      </StyledCard>
    </Container>
  );
};
